<!--
  * Descripción del componente y comportamiento:
  * Permite establecer la configuración que debe tener el escaner de códigos de barras.
-->
<template>
  <div class="main-uc-code-scanner-config">
    <div class="block-title">
      {{ title }}
    </div>
    <div v-if="configTemp != null">
      <f7-list class="padding">
        <template v-if="!hideOptionContinuousRead">
          <f7-list-item
            v-if="'LecturaEnTraspaso' in configTemp"
          >
            <span class="span-texto-grisAzul-14-400">
              {{ $t('production.codeScannerConfig.readBoxesInTransfer') }}
            </span>
            <label class="toggle toggle-init color-blue">
              <input
                type="checkbox"
                :checked="configTemp.LecturaEnTraspaso"
                :value="configTemp.LecturaEnTraspaso"
                @change="configTemp.LecturaEnTraspaso = !configTemp.LecturaEnTraspaso"
              >
              <span class="toggle-icon" />
            </label>
          </f7-list-item>
          <f7-list-item>
            <span class="span-texto-grisAzul-14-400">
              {{ $t('production.codeScannerConfig.asyncContinuousRead') }}
            </span>
            <label class="toggle toggle-init color-blue">
              <input
                type="checkbox"
                :checked="configTemp.LecturaContinuaCodigosBarras"
                :value="configTemp.LecturaContinuaCodigosBarras"
                @change="toggleContinuousRead"
              >
              <span class="toggle-icon" />
            </label>
          </f7-list-item>
          <f7-list-item v-if="!hideOptionContinuousReadManual">
            <span class="span-texto-grisAzul-14-400">
              {{ $t('production.codeScannerConfig.manualContinuousRead') }}
            </span>
            <label class="toggle toggle-init color-blue">
              <input
                type="checkbox"
                :checked="configTemp.LecturaContinuaCodigosBarrasManual"
                :value="configTemp.LecturaContinuaCodigosBarrasManual"
                @change="toggleContinuousManualRead"
              >
              <span class="toggle-icon" />
            </label>
          </f7-list-item>
        </template>
        <f7-list-item v-if="!hideOptionUsingFlash">
          <span class="span-texto-grisAzul-14-400">
            {{ $t('production.codeScannerConfig.usingFlash') }}
          </span>
          <label class="toggle toggle-init color-blue">
            <input
              type="checkbox"
              :checked="configTemp.UtilizarFlash"
              :value="configTemp.UtilizarFlash"
              @change="configTemp.UtilizarFlash = !configTemp.UtilizarFlash"
            >
            <span class="toggle-icon" />
          </label>
        </f7-list-item>
        <f7-list-item
          v-if="!hideOptionOrientation"
          class="smart-select-orientation"
          smart-select
          :smart-select-params="{ openIn: 'popover', closeOnSelect: true, setValueText: true }"
        >
          <template #title>
            <span>
              {{ $t('production.codeScannerConfig.orientation.title') }}
            </span>
          </template>
          <select
            name="Orientación"
            :value="configTemp.OrientacionCamaraLecturaCodigos"
            @change="configTemp.OrientacionCamaraLecturaCodigos = /** @type {any} */ ($event.target).value"
          >
            <option
              value="portrait"
              selected
            >
              {{ $t('production.codeScannerConfig.orientation.portrait') }}
            </option>
            <option value="landscape">
              {{ $t('production.codeScannerConfig.orientation.landscape') }}
            </option>
          </select>
        </f7-list-item>
      </f7-list>
    </div>
  </div>
</template>

<script setup>
// @ts-check
import { onMounted, ref } from 'vue';

/** @import { PropType, Ref } from 'vue' */
/** @typedef {{
 * LecturaContinuaCodigosBarras: boolean;
 * LecturaContinuaCodigosBarrasManual: boolean;
 * UtilizarFlash: boolean;
 * OrientacionCamaraLecturaCodigos: 'portrait' | 'landscape';
 * LecturaEnTraspaso: boolean;
 * }} UCCodeScannerConfigType */

const props = defineProps({
  config: {
    type: /** @type {PropType<Partial<UCCodeScannerConfigType>>} */ (Object),
    required: true,
  },
  title: {
    type: String,
    default: 'Configuración Escáner',
  },
  hideOptionContinuousRead: {
    type: Boolean,
    default: false,
  },
  hideOptionOrientation: {
    type: Boolean,
    default: false,
  },
  hideOptionUsingFlash: {
    type: Boolean,
    default: false,
  },
  hideOptionContinuousReadManual: {
    type: Boolean,
    default: false,
  },
});

/** @type {Ref<Partial<UCCodeScannerConfigType> | null>} */
const configTemp = ref(null);

/**
 * @public
 * @returns {void}
 */
function initialize() {
  configTemp.value = { ...props.config };
}

/**
 * @public
 * @returns {UCCodeScannerConfigType}
 */
function getNewConfig() {
  const config = configTemp.value ?? {};
  return {
    LecturaContinuaCodigosBarras: !!config.LecturaContinuaCodigosBarras,
    LecturaContinuaCodigosBarrasManual: !!config.LecturaContinuaCodigosBarrasManual,
    UtilizarFlash: !!config.UtilizarFlash,
    OrientacionCamaraLecturaCodigos: config.OrientacionCamaraLecturaCodigos ?? 'portrait',
    LecturaEnTraspaso: !!config.LecturaEnTraspaso,
  };
}

function toggleContinuousRead() {
  if (!configTemp.value) {
    return;
  }

  const newVal = !configTemp.value.LecturaContinuaCodigosBarras;
  configTemp.value.LecturaContinuaCodigosBarras = newVal;

  if (newVal) {
    configTemp.value.LecturaContinuaCodigosBarrasManual = false;
  }
}

function toggleContinuousManualRead() {
  if (!configTemp.value) {
    return;
  }

  const newVal = !configTemp.value.LecturaContinuaCodigosBarrasManual;
  configTemp.value.LecturaContinuaCodigosBarrasManual = newVal;

  if (newVal) {
    configTemp.value.LecturaContinuaCodigosBarras = false;
  }
}

onMounted(() => {
  initialize();
});

defineExpose({
  getNewConfig,
  initialize,
});
</script>

<style lang="scss">
.main-uc-code-scanner-config {
  .block-title {
    color: #8e8e93 !important;
    font-size: inherit;
    margin: 25px 0 15px 14px;
  }

  .smart-select-orientation {
    .smart-select {
      .item-content {
        .item-inner {
          .item-title {
            color: var(--his-steel-blue);
            display: flex !important;
          }

          .item-after {
            width: 15%;
          }
        }
      }
    }
  }
}
</style>
